import React from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Breadcrumbs,
  Button,
  Paper,
  Divider,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  ArrowLeft,
  Handshake,
  Globe,
  Users,
  Rocket,
  CheckCircle,
} from "lucide-react";
import PageShell from "../components/layout/PageShell";
import { getPartnerById } from "../api/business";

export default function PartnerDetailPage() {
 const { id } = useParams();

const {
  data: partner,
  isLoading,
  isError,
  error,
} = useQuery({
  queryKey: ["partner-detail", id],
  queryFn: async () => {
    console.log("Partner ID:", id);

    const response = await getPartnerById(id);
    console.log("Partner API response:", response);

    return response;
  },
});

console.log("Partner data:", partner);
console.log("Partner error:", error);

  const blue = "#2458D3";
  const navy = "#071B3A";

  const partnerType =
    partner?.partner_type?.replace(/_/g, " ") || "Business Partner";

  const details = [
    {
      label: "Partner type",
      value: partnerType,
      icon: Handshake,
    },
    {
      label: "Organization",
      value: partner?.name || "Our Partner",
      icon: Building2,
    },
    {
      label: "Partnership",
      value: "Professional",
      icon: Users,
    },
  ];

  return (
    <PageShell>
      <Box sx={{ bgcolor: "#F5F8FC", minHeight: "100vh", pb: 8 }}>
        {/* Premium blue header */}
        <Box
          sx={{
            background:
              "linear-gradient(120deg, #071B3A 0%, #123D83 55%, #2458D3 100%)",
            color: "#FFFFFF",
            pt: { xs: 3, md: 4 },
            pb: { xs: 10, md: 12 },
          }}
        >
          <Container maxWidth="lg">
            <Breadcrumbs
              separator="/"
              sx={{
                mb: 3,
                "& .MuiBreadcrumbs-separator": {
                  color: "rgba(255,255,255,0.6)",
                },
              }}
            >
              <Typography
                component={Link}
                to="/"
                sx={{
                  color: "#D1DDF2",
                  fontSize: 13,
                  textDecoration: "none",
                }}
              >
                Home
              </Typography>

              <Typography
                component={Link}
                to="/business?tab=clients-partners"
                sx={{
                  color: "#D1DDF2",
                  fontSize: 13,
                  textDecoration: "none",
                }}
              >
                Clients &amp; Partners
              </Typography>

              <Typography
                sx={{
                  color: "#FFFFFF",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {partner?.name || "Partner Details"}
              </Typography>
            </Breadcrumbs>

            {isLoading ? (
              <CircularProgress sx={{ color: "#FFFFFF" }} />
            ) : isError || !partner ? (
              <Typography sx={{ color: "#FFFFFF" }}>
                Unable to load partner details.
              </Typography>
            ) : (
              <>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
                >
                  <Chip
                    icon={<Handshake size={15} color="#FFFFFF" />}
                    label="OUR PARTNERS"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.16)",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      "& .MuiChip-icon": { color: "#FFFFFF" },
                    }}
                  />

                  <Chip
                    label="PROFESSIONAL"
                    sx={{
                      bgcolor: "#FFFFFF",
                      color: navy,
                      fontWeight: 700,
                    }}
                  />
                </Stack>

                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: 32, md: 54 },
                    lineHeight: 1.15,
                    fontWeight: 800,
                    letterSpacing: "-1.2px",
                    mb: 1.5,
                  }}
                >
                  {partner.name}
                </Typography>

                <Typography
                  sx={{
                    color: "#DCE8FF",
                    fontSize: { xs: 15, md: 19 },
                    maxWidth: 750,
                    lineHeight: 1.7,                  }}
                >
                  Building strong partnerships through collaboration,
                  technology, and shared business goals.
                </Typography>
              </>
            )}
          </Container>
        </Box>

        {/* Main content */}
        {!isLoading && !isError && partner && (
          <Container
            maxWidth="lg"
            sx={{
              mt: { xs: -6, md: -9 },
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1.8fr) minmax(290px, 0.95fr)",
                },
                gap: { xs: 3, md: 4 },
                alignItems: "start",
              }}
            >
              {/* Left column */}
              <Box sx={{ minWidth: 0 }}>
                {/* Partner logo card */}
                <Paper
                  elevation={0}
                  sx={{
                    height: { xs: 230, sm: 320, md: 390 },
                    borderRadius: "24px",
                    overflow: "hidden",
                    bgcolor: "#FFFFFF",
                    border: "4px solid #FFFFFF",
                    boxShadow: "0 20px 45px rgba(7,27,58,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {partner.logo ? (
                    <Box
                      component="img"
                      src={partner.logo}
                      alt={partner.name}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        p: 3,
                      }}
                    />
                  ) : (
                    <Stack
                      alignItems="center"
                      spacing={2}
                      sx={{ color: blue }}
                    >
                      <Building2 size={90} strokeWidth={1.3} />
                      <Typography
                        sx={{
                          fontSize: { xs: 22, md: 32 },
                          fontWeight: 800,
                          color: navy,
                          textAlign: "center",
                          px: 2,
                        }}
                      >
                        {partner.name}
                      </Typography>
                    </Stack>
                  )}
                </Paper>

                {/* Information cards */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 1.5,
                    mt: 2.5,
                  }}
                >
                  {details.map((item) => {
                    const Icon = item.icon;

                    return (
                      <Paper
                        key={item.label}
                        elevation={0}
                        sx={{
                          p: 2.2,
                          minHeight: 112,
                          borderRadius: "18px",
                          color: "#FFFFFF",
                          background:
                            "linear-gradient(135deg, #17438F 0%, #2864E8 100%)",
                          boxShadow:
                            "0 10px 24px rgba(36,88,211,0.18)",
                        }}
                      >
                        <Icon size={23} />

                        <Typography
                          sx={{
                            mt: 1.5,
                            fontSize: 16,
                            fontWeight: 800,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {item.value}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#DCE8FF",
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>

                {/* About partner */}
                <Paper
                  elevation={0}
                  sx={{
                    mt: 3,
                    p: { xs: 2.5, md: 3.5 },
                    borderRadius: "20px",
                    border: "1px solid #E1E8F2",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 25,
                      fontWeight: 800,
                      color: navy,
                      mb: 2,
                    }}
                  >
                    About this partner
                  </Typography>

                  {partner.description ? (
                    <Typography
                      sx={{
                        fontSize: 15,
                        color: "#475569",
                        lineHeight: 1.9,
                        whiteSpace: "pre-line",
                        textAlign: "justify",
                      }}
                    >
                      {partner.description}
                    </Typography>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: 15,
                        color: "#475569",
                        lineHeight: 1.9,
                      }}
                    >
                      {partner.name} is part of our partner network,
                      supporting collaboration and professional growth.
                    </Typography>
                  )}
                </Paper>
              </Box>

              {/* Right column */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  position: { xs: "static", md: "sticky" },
                  top: { md: "110px" },
                  alignSelf: "flex-start",
                  width: "100%",
                }}
              >
                {/* Call to action */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3.2,
                    borderRadius: "24px",
                    color: "#FFFFFF",
                    position: "relative",
                    overflow: "hidden",
                    background:
                      "linear-gradient(145deg, #173F91 0%, #2864E8 100%)",
                    boxShadow: "0 18px 35px rgba(15,55,125,0.24)",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: 140,
                      height: 140,
                      borderRadius: "50%",
                      bgcolor: "rgba(255,255,255,0.12)",
                      top: -65,
                      right: -40,
                    },
                  }}
                >
                  <Rocket size={27} />

                  <Typography
                    sx={{
                      mt: 2,
                      fontSize: 24,
                      fontWeight: 800,
                    }}
                  >
                    Stronger together
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1,
                      mb: 2.5,
                      fontSize: 13,
                      color: "#E2EAFF",
                      lineHeight: 1.8,
                    }}
                  >
                    Discover our network of partners and learn more
                    about our collaborative business ecosystem.
                  </Typography>

                  <Button
                    component={Link}
                    to="/business?tab=clients-partners"
                    fullWidth
                    variant="contained"
                    sx={{
                      py: 1.5,
                      mb: 1.2,
                      borderRadius: "12px",
                      bgcolor: "#FFFFFF",
                      color: navy,
                      fontWeight: 800,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#EAF0FF",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Explore partners
                  </Button>
                </Paper>

                {/* Partner details */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "22px",
                    border: "1px solid #E1E8F2",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 800,
                      color: navy,
                      mb: 2.5,
                    }}
                  >
                    Partner details
                  </Typography>

                  {details.map((item, index) => (
                    <React.Fragment key={item.label}>
                      {index > 0 && <Divider sx={{ my: 1.8 }} />}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 14, color: "#475569" }}
                        >
                          {item.label}
                        </Typography>

                        <Chip
                          size="small"
                          label={item.value}
                          sx={{
                            height: "auto",
                            maxWidth: "65%",
                            bgcolor: "#EAF1FF",
                            color: "#2452A5",
                            fontWeight: 600,
                            "& .MuiChip-label": {
                              display: "block",
                              whiteSpace: "normal",
                              py: 0.7,
                              textAlign: "right",
                              overflowWrap: "anywhere",
                            },
                          }}
                        />
                      </Box>
                    </React.Fragment>
                  ))}

                  <Divider sx={{ my: 2 }} />

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Globe size={19} color={blue} />
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#475569",
                        lineHeight: 1.7,
                      }}
                    >
                      Connecting businesses through trusted partnerships.
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 2 }}
                  >
                    <CheckCircle size={19} color={blue} />
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#475569",
                        lineHeight: 1.7,
                      }}
                    >
                      Collaboration focused on shared success.
                    </Typography>
                  </Stack>
                </Paper>

                <Button
                  component={Link}
                  to="/business?tab=clients-partners"
                  startIcon={<ArrowLeft size={17} />}
                  sx={{
                    justifyContent: "flex-start",
                    color: blue,
                    fontWeight: 700,
                    textTransform: "none",
                  }}
                >
                  Back to all partners
                </Button>
              </Box>
            </Box>
          </Container>
        )}
      </Box>
    </PageShell>
  );
}
